import { create } from 'apisauce';
import { Settings, Interval, DateTime } from 'luxon';
import qs from 'qs';
import * as TillerConsole from '@tiller/logger-sdk';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * Customs errors for Report-SDK.
 */
/**
 * ValidationError.
 * Error thrown when a validation fail.
 *
 * @extends Error
 * @class ValidationError
 */
class ValidationError extends Error {
    /**
     * Create a new instance of ValidationError.
     *
     * @param errorMessage - The validation error
     * @param field - which field is concerned by the error
     * @param value - received value
     */
    constructor(errorMessage, field, value) {
        const message = `ValidationError - ${field}: ${errorMessage} (got ${value})`;
        super(message);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ValidationError);
        }
        // Custom field
        this.errorMessage = errorMessage;
        this.field = field;
        this.value = value;
    }
}

/**
 * This class is responsible of Pagination model.
 * VALIDATE & formats sort as api query params.
 */
class Pagination {
    constructor(limit, offset) {
        this.limit = limit ? parseInt(String(limit), 10) : null;
        this.offset = offset ? parseInt(String(offset), 10) : null;
    }
    /**
     * Formats limit for query params
     */
    formatLimit() {
        return this.limit !== null ? this.limit : undefined;
    }
    /**
     * Formats offset for query params
     */
    formatOffset() {
        return this.limit !== null && this.offset !== null ? this.offset : undefined;
    }
    /**
     * Throws error if object is invalid
     */
    validate() {
        if (this.limit !== null && (!Number.isFinite(this.limit) || this.limit < 1)) {
            throw new ValidationError('must be an integer greater than zero', 'limit', this.limit);
        }
        // Limit is mandatory if offset is defined
        if (this.offset !== null && this.limit === null) {
            throw new ValidationError('must be provided with offset', 'limit', this.limit);
        }
        if (this.offset !== null && (!Number.isFinite(this.offset) || this.offset < 1)) {
            throw new ValidationError('must be an integer greater than zero', 'offset', this.offset);
        }
    }
}

/**
 * This class is responsible of Period model.
 * from & to params need to be ISO dates
 */
class Period {
    constructor(from, to, timezone = Settings.defaultZoneName) {
        this.timezone = timezone;
        if (from || to) {
            this.interval = Interval.fromDateTimes(DateTime.fromISO(from || ''), DateTime.fromISO(to || ''));
        }
    }
    /**
     * Format from date.
     *
     * @return ISO format.
     */
    formatFrom() {
        return this.interval ? this.interval.start.toISO({ includeOffset: false }) : undefined;
    }
    /**
     * Format from date.
     *
     * @return ISO format.
     */
    formatTo() {
        return this.interval ? this.interval.end.toISO({ includeOffset: false }) : undefined;
    }
    /**
     * Gets the Period TimeZone.
     *
     * @return the timezone.
     */
    formatTimezone() {
        return encodeURIComponent(this.timezone); // FIXME: should not be encoded (fixme server-side too).
    }
    validate() {
        if (!this.interval)
            return;
        if (!this.interval.isValid) {
            throw new ValidationError(String(this.interval.invalidReason), 'period', this.interval);
        }
    }
}

/**
 * Class responsible of API response.
 *
 * @class Results
 */
class Result {
    /**
     * Create a new instance of Result.
     * The Result is the model that will be created after an API call.
     *
     * @param api response status, ok, data, problem.
     *
     * @return new instance of Result.
     */
    constructor({ status, ok, data, problem }) {
        this.statusCode = status;
        this.ok = ok;
        this.data = data;
        this.problem = problem;
    }
    /**
     * Gets the Result boolean.
     *
     * @return true || false, depending on the API call success.
     */
    isSuccess() {
        return this.ok;
    }
    /**
     * Gets response period.
     *
     * @return response data period. Must include "from" and "to" keys
     * @throws {Error} if period key does not exists.
     */
    getPeriod() {
        return (this.data !== undefined && 'period' in this.data && this.data.period) || null;
    }
    /**
     * Gets response data.
     *
     * @return response data.
     * @throws {Error} if data does not exists.
     */
    getData() {
        return (this.data !== undefined && 'data' in this.data && this.data.data) || null;
    }
    /**
     * Gets the Error Code.
     *
     * @return response error code.
     */
    getErrorCode() {
        return (this.data !== undefined && 'code' in this.data && this.data.code) || null;
    }
    /**
     * Gets the Error message.
     *
     * @return error message from HTTP response.
     */
    getErrorMessage() {
        return (this.data !== undefined && 'message' in this.data && this.data.message) || undefined;
    }
    /**
     * Gets response statusCode.
     * StatusCode will be one of the existing HTTP statusCode.
     *
     * @return statusCode.
     */
    getStatusCode() {
        return this.statusCode;
    }
}

/**
 * Existing aggregate for `by` field.
 */
var TimeAggregate;
(function (TimeAggregate) {
    TimeAggregate["Hour"] = "hour";
    TimeAggregate["Day"] = "day";
    TimeAggregate["Week"] = "week";
    TimeAggregate["CustomWeek"] = "custom_week";
    TimeAggregate["Month"] = "month";
    TimeAggregate["Year"] = "year";
})(TimeAggregate || (TimeAggregate = {}));
/**
 * Existing aggregate for `groups` field in table context.
 */
var DataAggregate;
(function (DataAggregate) {
    DataAggregate["Product"] = "product";
    DataAggregate["Category"] = "category";
    DataAggregate["Meal"] = "meal";
    DataAggregate["Menu"] = "menu";
    DataAggregate["ProductType"] = "product_type";
    DataAggregate["Summary"] = "summary";
    DataAggregate["Seller"] = "seller";
})(DataAggregate || (DataAggregate = {}));
/**
 * Existing reference for `reference` field.
 */
var Reference;
(function (Reference) {
    Reference["Nearest"] = "nearest";
    Reference["PreviousYear"] = "previous_year";
})(Reference || (Reference = {}));
/**
 * Sortable columns
 */
var SortColumn;
(function (SortColumn) {
    SortColumn["Revenue"] = "revenue";
    SortColumn["Quantity"] = "quantity";
})(SortColumn || (SortColumn = {}));
/**
 * Filterable columns
 */
var FilterColumn;
(function (FilterColumn) {
    FilterColumn["Product"] = "product";
    FilterColumn["Category"] = "category";
    FilterColumn["Meal"] = "meal";
    FilterColumn["Menu"] = "menu";
    FilterColumn["ProductType"] = "product_type";
})(FilterColumn || (FilterColumn = {}));

/**
 * Resources.
 */
var Resource;
(function (Resource) {
    Resource["AverageDuration"] = "average-duration";
    Resource["AverageSpent"] = "average-spent";
    Resource["CustomersCount"] = "customers-count";
    Resource["Pending"] = "pending";
    Resource["ProductType"] = "product-type";
    Resource["Revenue"] = "revenue";
    Resource["Sales"] = "sales";
    Resource["Synthesis"] = "synthesis";
})(Resource || (Resource = {}));
// Existing KPI resources.
const KPIResources = [
    Resource.AverageDuration,
    Resource.AverageSpent,
    Resource.CustomersCount,
    Resource.Pending,
    Resource.Revenue,
];
// Existing Chart resources.
const ChartResources = [
    Resource.AverageDuration,
    Resource.AverageSpent,
    Resource.CustomersCount,
    Resource.Revenue,
    Resource.ProductType,
];
// Existing Export resources.
const ExportResources = [Resource.Synthesis, Resource.Sales];
// Existing Table resources.
const TableResources = [Resource.Sales];

/**
 * This class is responsible of Sort model.
 * Validates & formats sort as api query params.
 */
class Sort {
    constructor(column, order = 'ASC') {
        this.column = column;
        this.order = order;
    }
    /**
     * Format Sort parameter
     */
    formatSort() {
        return this.column ? `${this.order === 'DESC' ? '-' : '+'}${this.column}` : undefined;
    }
    /**
     * Throws error if object is invalid
     */
    validate() {
        if (this.column && !Object.values(SortColumn).includes(this.column)) {
            throw new ValidationError('is not a sortable column', 'sortColumn', this.column);
        }
        if (this.order && this.column && this.order !== 'ASC' && this.order !== 'DESC') {
            throw new ValidationError('must be "ASC" | "DESC"', 'sortOrder', this.order);
        }
    }
}

/**
 * Validates required params, and throws a ValidationError if a field is missing.
 *
 * @param params - the params object to validate
 * @param options.fields - the properties that are required
 */
const validateRequired = (params, { fields = [] }) => {
    fields.forEach(field => {
        if (!params[field]) {
            throw new ValidationError('field is required', field, params[field]);
        }
    });
};
/**
 * Validates the length of an array or a string, and throws a ValidationError if field is invalid.
 *
 * @param value - the value to validate
 * @param options.field - the name of the field
 * @param options.min - the minimum length to be valid
 * @param options.max - the maximum length to be valid
 */
const validateLength = (value, { field = 'field', min, max }) => {
    if (value instanceof Array || typeof value === 'string') {
        if (min && value.length < min) {
            throw new ValidationError(`must have a min length of ${min}`, field, value.length);
        }
        if (max && value.length > max) {
            throw new ValidationError(`must have a max length of ${max}`, field, value.length);
        }
    }
    else {
        throw new ValidationError('unknown length', field, value);
    }
    return true;
};
/**
 * Validates resource field for getKPI, and throws a ValidationError if field is invalid.
 * Resource must be one of KPIResource
 *
 * @param resource the value to validate
 */
const validateResourceKpi = (resource) => {
    if (!KPIResources.includes(resource)) {
        throw new ValidationError('not a valid resource', 'resource', resource);
    }
    return true;
};
/**
 * Validates resource field for getChart, and throws a ValidationError if field is invalid.
 * Resource must be one of ChartResource
 *
 * @param resource - the value to validate
 */
const validateResourceChart = (resource) => {
    if (!ChartResources.includes(resource)) {
        throw new ValidationError('not a valid resource', 'resource', resource);
    }
    return true;
};
/**
 * Validates resource field for getTable, and throws a ValidationError if field is invalid.
 * Resource must be one of TableResource
 *
 * @param resource - the value to validate
 */
const validateResourceTable = (resource) => {
    if (!TableResources.includes(resource)) {
        throw new ValidationError('not a valid resource', 'resource', resource);
    }
    return true;
};
/**
 * Validates resource field for getExport, and throws a ValidationError if field is invalid.
 * Resource must be one of ExportResource
 *
 * @param resource - the value to validate
 */
const validateResourceExport = (resource) => {
    if (!ExportResources.includes(resource)) {
        throw new ValidationError('not a valid resource', 'resource', resource);
    }
    return true;
};
/**
 * Validates reference field, and throws a ValidationError if field is invalid.
 * Resource must be one of Reference
 *
 * @param reference - the value to validate
 */
const validateReference = (reference) => {
    if (reference && !Object.values(Reference).includes(reference)) {
        throw new ValidationError('not a valid reference', 'reference', reference);
    }
    return true;
};
/**
 * Validates by field, and throws a ValidationError if field is invalid.
 * Resource must be one of TimeAggregate
 *
 * @param by - the value to validate
 */
const validateBy = (by) => {
    if (by && !Object.values(TimeAggregate).includes(by)) {
        throw new ValidationError('not a valid time aggregate', 'by', by);
    }
    return true;
};
/**
 * Validates groups field, and throws a ValidationError if field is invalid.
 * groups values must be one of DataAggregate
 *
 * @param groups - the values to validate
 */
const validateGroups = (groups) => {
    if (!(groups instanceof Array)) {
        throw new ValidationError('must be an array', 'groups', groups);
    }
    if (groups) {
        groups.forEach(group => {
            if (!Object.values(DataAggregate).includes(group)) {
                throw new ValidationError('contains an invalid data aggregate', 'groups', group);
            }
        });
    }
    return true;
};
/**
 * Validates filters field, and throws a ValidationError if field is invalid.
 * Filters must be an object, with keys corresponding to a filter, and values being a string or an array of string of accepted values.
 *
 * @param filters - the values to validate
 */
const validateFilters = (filters) => {
    if (filters) {
        if (!(filters instanceof Object)) {
            throw new ValidationError('must be an object', 'filters', filters);
        }
        Object.keys(filters).forEach(filterKey => {
            if (!Object.values(FilterColumn).includes(filterKey)) {
                throw new ValidationError('contains a non-filterable column', 'filters', filterKey);
            }
            const filter = filters[filterKey];
            if (typeof filter !== 'string' &&
                !(filter instanceof Array && filter.every(value => typeof value === 'string'))) {
                throw new ValidationError('must be a string or an array of strings', `filters[${filterKey}]`, filter);
            }
        });
    }
    return true;
};
/**
 * Validates limits field, and throws a ValidationError if field is invalid.
 * End dates must be an array of object with a number storeId and a string date properties
 *
 * @param limits - the values to validate
 * @param period - the period to validate against if given
 */
const validateLimits = (limits, period) => {
    if (!limits) {
        return true;
    }
    if (!Array.isArray(limits)) {
        throw new ValidationError('must be an array', `limits`, limits);
    }
    const isLimitLike = (limit) => typeof limit === 'object' && limit !== null && 'storeId' in limit && 'endDateLimit' in limit;
    if (!limits.every((limit, index) => {
        if (!isLimitLike(limit) ||
            typeof limit.storeId !== 'number' ||
            (typeof limit.endDateLimit !== 'string' && !(limit.endDateLimit instanceof DateTime))) {
            throw new ValidationError('must be an object with a number storeId and a string endDateLimit property', `limits[${index}]`, limit);
        }
        const endDateLimit = limit.endDateLimit instanceof DateTime
            ? limit.endDateLimit
            : DateTime.fromISO(limit.endDateLimit);
        if (!endDateLimit.isValid) {
            throw new ValidationError(String(endDateLimit.invalidReason), `limits[${index}]`, limit.endDateLimit);
        }
        if ((period === null || period === void 0 ? void 0 : period.interval) && endDateLimit > period.interval.end) {
            throw new ValidationError(`must be before period end date: ${period.interval.end.toISO()}`, `limits[${index}].endDateLimit`, limit.endDateLimit);
        }
        return true;
    })) {
        throw new ValidationError('must be an array of objects with a number storeId and a string endDateLimit property', `limits`, limits);
    }
    return true;
};

/**
 * Gets the pre-request transform.
 *
 * @param getToken - Function that gets a token.
 */
const getPreRequestTransform = (getToken) => request => {
    request.headers.Authorization = `Bearer ${getToken()}`;
};
/**
 * Formats query params to a query string.
 *
 * @return {string}
 */
const paramsSerializer = (params) => qs.stringify(params, { arrayFormat: 'repeat', allowDots: true });
/**
 * This class implements the report manager that is an interface to the API.
 */
class ApiReportManager {
    /**
     * Creates a new instance of the manager.
     *
     * @param baseURL - The base URL to the API.
     * @param getToken - A function that provides a token to authorize requests.
     * @param version - Desired API version (1.0, 2.0...)
     * @throws {Error} - If no base URL or token is given.
     */
    constructor(baseURL, getToken, version = '1.0') {
        if (!baseURL) {
            throw new ValidationError('argument is required', 'baseURL', baseURL);
        }
        if (!getToken) {
            throw new ValidationError('argument is required', 'getToken', getToken);
        }
        if (!['1.0', '2.0'].includes(version)) {
            TillerConsole.debug(`[ApiReportManager] Incorrect version number (You specified ${version}). Using v1.0 by default.`);
            this.version = '1.0';
        }
        else {
            this.version = version;
        }
        this.paramSerializer = paramsSerializer;
        this.api = create({
            baseURL,
            paramsSerializer: this.paramSerializer,
        });
        this.api.addRequestTransform(getPreRequestTransform(getToken));
    }
    /**
     * Get Filters with all possible values present in data.
     *
     * @return a new instance of Result.
     */
    getFilters() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.get(`report/filters${this.version === '2.0' ? '-v2' : ''}`);
            return new Result(response);
        });
    }
    getKPI(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, from, to, reference, applyDailyTimeslot, schoolMode, limits } = params;
            validateRequired(params, { fields: ['resource', 'from', 'to'] });
            validateResourceKpi(resource);
            validateReference(reference);
            const period = new Period(from, to);
            period.validate();
            validateLimits(limits, period);
            const data = {
                from: period.formatFrom(),
                to: period.formatTo(),
                timezone: period.formatTimezone(),
                apply_daily_timeslot: !!applyDailyTimeslot,
                school_mode: !!schoolMode,
                reference: reference || undefined,
                limits: limits === null || limits === void 0 ? void 0 : limits.map(({ storeId, endDateLimit }) => ({
                    store_id: storeId,
                    end_date_limit: (typeof endDateLimit === 'string'
                        ? DateTime.fromISO(endDateLimit)
                        : endDateLimit).toISO({ includeOffset: false }),
                })),
            };
            const response = yield this.api.get(`report/kpis${this.version === '2.0' ? '-v2' : ''}/${resource}`, data);
            return new Result(response);
        });
    }
    /**
     * Gets Chart.
     * This method allows you to retrieve a chart depending on a period and the resource you look for.
     *
     * @throws {ValidationError} if params are invalid
     */
    getChart(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, from, to, by, reference, applyDailyTimeslot, schoolMode, limits } = params;
            validateRequired(params, { fields: ['resource', 'from', 'to', 'by'] });
            validateResourceChart(resource);
            validateReference(reference);
            validateBy(by);
            const period = new Period(from, to);
            period.validate();
            validateLimits(limits, period);
            const data = {
                from: period.formatFrom(),
                to: period.formatTo(),
                timezone: period.formatTimezone(),
                apply_daily_timeslot: !!applyDailyTimeslot,
                school_mode: !!schoolMode,
                reference: reference || undefined,
                by,
                limits: limits === null || limits === void 0 ? void 0 : limits.map(({ storeId, endDateLimit }) => ({
                    store_id: storeId,
                    end_date_limit: (typeof endDateLimit === 'string'
                        ? DateTime.fromISO(endDateLimit)
                        : endDateLimit).toISO({ includeOffset: false }),
                })),
            };
            const response = yield this.api.get(`report/charts${this.version === '2.0' ? '-v2' : ''}/${resource}`, data);
            return new Result(response);
        });
    }
    /**
     * Gets Table.
     * This method allows you to retrieve a table depending on a period and the resource you look for.
     *
     * @throws {ValidationError} if params are invalid
     */
    getTable(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, from, to, groups, by, applyDailyTimeslot, sortColumn, sortOrder, offset, limit, filters, schoolMode, limits, } = params;
            validateRequired(params, { fields: ['resource', 'from', 'to', 'groups'] });
            validateResourceTable(resource);
            validateBy(by);
            validateLength(groups, { field: 'groups', min: 1 });
            validateGroups(groups);
            validateFilters(filters);
            const period = new Period(from, to);
            period.validate();
            validateLimits(limits, period);
            const sort = new Sort(sortColumn, sortOrder);
            sort.validate();
            const pagination = new Pagination(limit, offset);
            pagination.validate();
            const data = {
                from: period.formatFrom(),
                to: period.formatTo(),
                timezone: period.formatTimezone(),
                groups,
                by: by || undefined,
                apply_daily_timeslot: !!applyDailyTimeslot,
                school_mode: !!schoolMode,
                sort: sort.formatSort(),
                limit: pagination.formatLimit(),
                offset: pagination.formatOffset(),
                filter: filters || undefined,
                limits: limits === null || limits === void 0 ? void 0 : limits.map(({ storeId, endDateLimit }) => ({
                    store_id: storeId,
                    end_date_limit: (typeof endDateLimit === 'string'
                        ? DateTime.fromISO(endDateLimit)
                        : endDateLimit).toISO({ includeOffset: false }),
                })),
            };
            const response = yield this.api.get(`report/tables${this.version === '2.0' ? '-v2' : ''}/${resource}`, data);
            return new Result(response);
        });
    }
    /**
     * Get export for resource
     * This method allows you to retrieve any export depending on a period.
     *
     * @param {string} resource - Resource you want to retrieve for the export.
     * @param {string} from - Start of the period you want to retrieve for the export.
     * @param {string} to - End of the period you want to retrieve for the export.
     * @param {string} by - Time aggregate used for the export.
     * @param {array} groups - Aggregate used for the export
     * @param {boolean} applyDailyTimeslot - optional - Shall the timeslot be applied daily ?
     * @param {string} locale - optional - Locale in which the user wants his export
     *
     * @return {Result} a new instance of Result.
     *
     * @throws {ValidationError} if params are invalid
     */
    getExport(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { resource, from, to, groups, by, applyDailyTimeslot, locale, schoolMode, limits, } = params;
            validateRequired(params, { fields: ['resource', 'from', 'to'] });
            validateResourceExport(resource);
            validateBy(by);
            validateGroups(groups);
            if (resource === Resource.Synthesis) {
                validateRequired(params, { fields: ['by'] });
            }
            if (resource === Resource.Sales) {
                validateRequired(params, { fields: ['groups'] });
                validateLength(groups, { field: 'groups', min: 1 });
            }
            const period = new Period(from, to);
            period.validate();
            validateLimits(limits, period);
            const data = {
                from: period.formatFrom(),
                to: period.formatTo(),
                timezone: period.formatTimezone(),
                by,
                groups: groups || undefined,
                apply_daily_timeslot: !!applyDailyTimeslot,
                school_mode: !!schoolMode,
                locale: locale || 'en-GB',
                limits: limits === null || limits === void 0 ? void 0 : limits.map(({ storeId, endDateLimit }) => ({
                    store_id: storeId,
                    end_date_limit: (typeof endDateLimit === 'string'
                        ? DateTime.fromISO(endDateLimit)
                        : endDateLimit).toISO({ includeOffset: false }),
                })),
            };
            const response = yield this.api.get(`report/exports${this.version === '2.0' ? '-v2' : ''}/${resource}`, data);
            return new Result(response);
        });
    }
}

/**
 * Initializes the SDK.
 *
 * @param baseURL - The base URl to the API.
 * @param getToken - A function that gets a token to sign a request.
 *
 * @return An instance of catalog manager.
 */
function init({ baseURL, getToken, version = '1.0' }) {
    return new ApiReportManager(baseURL, getToken, version);
}

export { ApiReportManager, ChartResources, DataAggregate, ExportResources, FilterColumn, KPIResources, Pagination, Period, Reference, Resource, Result, Sort, SortColumn, TableResources, TimeAggregate, ValidationError, init };
